import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaCar, FaCalendarAlt, FaUser, FaPhone, FaEnvelope, FaIdCard, FaInfoCircle, FaBell, FaTools } from 'react-icons/fa';
import { useAuth } from '@/context/AuthContextApi';
import { useSucursal } from '@/context/SucursalContext';
import { UserButton } from '@clerk/clerk-react';
import Button from '@/components/common/Button';
import * as mantenimientosApiService from '@/services/mantenimientosApiService';
import * as citasApiService from '@/services/citasApiService';
import * as vehiculosApiService from '@/services/vehiculosApiService';
import moment from 'moment';

/**
 * Componente para el perfil del usuario cliente - optimizado para escritorio
 * @returns {JSX.Element} - Componente de perfil de usuario
 */
const UserProfile = () => {
  const { user, clienteData } = useAuth();
  const { selectedSucursal, sucursalData } = useSucursal();
  const navigate = useNavigate();
  
  // Estados para datos reales
  const [vehiculos, setVehiculos] = useState([]);
  const [citas, setCitas] = useState([]);
  const [mantenimientos, setMantenimientos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actividadReciente, setActividadReciente] = useState([]);

  // Cargar datos del cliente
  useEffect(() => {
    const cargarDatos = async () => {
      if (!clienteData?.id || !sucursalData?.id) {
        setLoading(false);
        return;
      }
      
      setLoading(true);
      try {
        // Cargar vehículos del cliente
        const vehiculosData = await vehiculosApiService.getVehiculos(clienteData.id);
        setVehiculos(vehiculosData || []);
        
        // Cargar citas del cliente
        const citasData = await citasApiService.getCitas(sucursalData.id, clienteData.id);
        setCitas(citasData || []);
        
        // Debug para verificar estados de citas
        //console.log('Citas cargadas:', citasData);
        if (citasData && citasData.length > 0) {
          //console.log('Campo finalizada de citas:', citasData.map(c => ({id: c.id, finalizada: c.finalizada})));
        }
        
        // Cargar mantenimientos del cliente
        const mantenimientosData = await mantenimientosApiService.getMantenimientos({
          sucursal_id: sucursalData.id,
          cliente_id: clienteData.id
        });
        setMantenimientos(mantenimientosData || []);
        
        // Generar actividad reciente
        generarActividadReciente(vehiculosData, citasData, mantenimientosData);
        
      } catch (error) {
        console.error('Error cargando datos del cliente:', error);
      } finally {
        setLoading(false);
      }
    };
    
    cargarDatos();
  }, [clienteData, sucursalData]);
  
  // Generar actividad reciente combinando datos
  const generarActividadReciente = (vehiculosData, citasData, mantenimientosData) => {
    const actividades = [];
    
    // Mantenimientos recientes
    if (mantenimientosData && mantenimientosData.length > 0) {
      const mantenimientoReciente = mantenimientosData
        .sort((a, b) => new Date(b.fecha) - new Date(a.fecha))[0];
      
      actividades.push({
        tipo: 'mantenimiento',
        icono: FaTools,
        color: 'green',
        titulo: 'Mantenimiento completado',
        descripcion: `Mantenimiento realizado para vehículo ${mantenimientoReciente.patente}`,
        fecha: mantenimientoReciente.fecha,
        fechaTexto: moment(mantenimientoReciente.fecha).fromNow()
      });
    }
    
    // Citas recientes
    if (citasData && citasData.length > 0) {
      const citaReciente = citasData
        .filter(c => c.finalizada === true)
        .sort((a, b) => new Date(b.fecha_hora) - new Date(a.fecha_hora))[0];
      
      if (citaReciente) {
        actividades.push({
          tipo: 'cita',
          icono: FaCalendarAlt,
          color: 'blue',
          titulo: 'Cita completada',
          descripcion: `Cita de ${citaReciente.servicios?.[0]?.nombre || 'mantenimiento'} completada`,
          fecha: citaReciente.fecha_hora,
          fechaTexto: moment(citaReciente.fecha_hora).fromNow()
        });
      }
    }
    
    // Registro del usuario
    if (user?.FechaRegistro) {
      actividades.push({
        tipo: 'registro',
        icono: FaUser,
        color: 'purple',
        titulo: 'Registro completado',
        descripcion: 'Bienvenido a Revvly. Su cuenta ha sido activada correctamente.',
        fecha: user.FechaRegistro,
        fechaTexto: moment(user.FechaRegistro).fromNow()
      });
    }
    
    // Ordenar por fecha y tomar las 3 más recientes
    const actividadesOrdenadas = actividades
      .sort((a, b) => new Date(b.fecha) - new Date(a.fecha))
      .slice(0, 3);
    
    setActividadReciente(actividadesOrdenadas);
  };
  
  // Calcular próxima cita
  const proximaCita = citas
    .filter(c => c.finalizada === false && new Date(c.fecha_hora) > new Date())
    .sort((a, b) => new Date(a.fecha_hora) - new Date(b.fecha_hora))[0];
  
  return (
    <div className="p-4 md:p-6 bg-gray-900">
      <h1 className="text-2xl font-bold mb-6 text-white">Mi Perfil</h1>
      
      {/* Layout principal con diseño de 2 columnas en escritorio */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Columna izquierda (1/3 del ancho) */}
        <div className="lg:col-span-1">
          {/* Tarjeta de perfil principal */}
          <div className="bg-gray-800 rounded-lg shadow-xl overflow-hidden mb-6 border border-gray-700">
            {/* Banner con gradiente */}
            <div className="bg-gradient-to-r from-blue-700 via-blue-600 to-indigo-700 h-24 relative">
              <div className="absolute inset-0 opacity-10 bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI1IiBoZWlnaHQ9IjUiPgo8cmVjdCB3aWR0aD0iNSIgaGVpZ2h0PSI1IiBmaWxsPSIjZmZmIj48L3JlY3Q+CjxyZWN0IHdpZHRoPSIxIiBoZWlnaHQ9IjEiIGZpbGw9IiMwMDAiPjwvcmVjdD4KPC9zdmc+')]"></div>
              
              {/* Avatar de usuario con borde - versión optimizada basada en DOM real */}
              <div className="absolute -bottom-14 left-1/2 transform -translate-x-1/2 bg-gray-800 rounded-full p-1 border-4 border-gray-800 shadow-lg ring-2 ring-blue-500">
                <div className="bg-gray-700 rounded-full h-28 w-28 flex items-center justify-center overflow-hidden border border-gray-600">
                  {/* Contenedor específico con la clase scale-[1.05] que vimos en el DOM */}
                  <div className="scale-[1.8] flex items-center justify-center">
                    <UserButton 
                      afterSignOutUrl="/taller"
                      appearance={{
                        elements: {
                          avatarBox: "cl-avatarBox cl-userButtonAvatarBox",
                          avatarImage: "cl-avatarImage cl-userButtonAvatarImage w-full h-full scale-[1.8]"
                        }
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
            
            {/* Información del usuario */}
            <div className="pt-20 px-6 pb-6 text-center">
              <h2 className="text-xl font-bold text-white">{user?.Nombre || 'Cliente'}</h2>
              <p className="text-blue-400 mb-4">{user?.Email || 'correo@ejemplo.com'}</p>
              
              <div className="grid grid-cols-1 gap-3 mt-6">
                <div className="flex items-center text-gray-300 bg-gray-750 p-3 rounded-lg border border-gray-700">
                  <FaIdCard className="text-blue-400 mr-3 text-lg" />
                  <span>Cliente desde: {new Date(user?.FechaRegistro || Date.now()).toLocaleDateString()}</span>
                </div>
                {clienteData?.Telefono && (
                  <div className="flex items-center text-gray-300 bg-gray-750 p-3 rounded-lg border border-gray-700">
                    <FaPhone className="text-blue-400 mr-3 text-lg" />
                    <span>Teléfono: {clienteData.Telefono}</span>
                  </div>
                )}
              </div>
              
              {/* Se ha eliminado la sección de Sucursal seleccionada */}
            </div>
          </div>

          {/* Panel de información */}
          <div className="bg-gray-800 rounded-lg p-6 shadow-lg border border-gray-700">
            <h3 className="text-lg font-semibold mb-4 flex items-center text-white">
              <FaInfoCircle className="text-blue-400 mr-2" />
              Información Importante
            </h3>
            
            <div className="space-y-4">
              <div className="border-l-4 border-blue-500 bg-blue-900 bg-opacity-20 rounded-r-lg p-3">
                <h4 className="font-medium text-blue-400 mb-1">Bienvenido a su panel personal</h4>
                <p className="text-gray-300 text-sm">
                  Desde aquí puede gestionar sus vehículos, agendar citas de mantenimiento y revisar su información de perfil.
                </p>
              </div>
              
              <div className="border-l-4 border-yellow-500 bg-yellow-900 bg-opacity-10 rounded-r-lg p-3">
                <h4 className="font-medium text-yellow-400 mb-1">Recordatorio</h4>
                <p className="text-gray-400 text-sm">
                  Para poder agendar citas, primero debe seleccionar una sucursal{' '}
                  <span className="hidden lg:inline">en el panel lateral izquierdo</span>
                  <span className="lg:hidden">desde el menú "Más"</span>.
                </p>
              </div>
            </div>
          </div>
        </div>
        
        {/* Columna derecha (2/3 del ancho) */}
        <div className="lg:col-span-2">
          {/* Fila superior - Tarjetas de acceso rápido */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            {/* Tarjeta de vehículos */}
            <div className="bg-gradient-to-br from-gray-800 to-gray-850 rounded-lg p-6 shadow-lg border border-gray-700 hover:border-blue-500 transition-all duration-300">
              <div className="flex items-start">
                <div className="bg-blue-600 bg-opacity-20 p-3 rounded-full mr-4">
                  <FaCar className="text-blue-400 text-2xl" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-white mb-2">Mis Vehículos</h3>
                  <p className="text-gray-400 mb-4 text-sm">
                    {loading ? 'Cargando...' : `${vehiculos.length} vehículo${vehiculos.length !== 1 ? 's' : ''} registrado${vehiculos.length !== 1 ? 's' : ''}`}
                  </p>
                  <Button
                    variant="primary"
                    onClick={() => navigate('/vehiculos')}
                    className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700"
                  >
                    Ir a mis vehículos
                  </Button>
                </div>
              </div>
            </div>
            
            {/* Tarjeta de citas */}
            <div className="bg-gradient-to-br from-gray-800 to-gray-850 rounded-lg p-6 shadow-lg border border-gray-700 hover:border-blue-500 transition-all duration-300">
              <div className="flex items-start">
                <div className="bg-blue-600 bg-opacity-20 p-3 rounded-full mr-4">
                  <FaCalendarAlt className="text-blue-400 text-2xl" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-white mb-2">Mis Citas</h3>
                  <p className="text-gray-400 mb-4 text-sm">
                    {selectedSucursal 
                      ? (loading ? 'Cargando...' : `${citas.length} cita${citas.length !== 1 ? 's' : ''} registrada${citas.length !== 1 ? 's' : ''}`)
                      : "Seleccione una sucursal para gestionar sus citas."}
                  </p>
                  <Button
                    variant="primary"
                    onClick={() => navigate('/citas')}
                    disabled={!selectedSucursal}
                    className={`w-full ${!selectedSucursal ? 'bg-gray-600 cursor-not-allowed' : 'bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700'}`}
                  >
                    {selectedSucursal ? "Ir a mis citas" : "Seleccione una sucursal"}
                  </Button>
                </div>
              </div>
            </div>
          </div>
          
          {/* Historial y actividad reciente */}
          <div className="bg-gray-800 rounded-lg shadow-lg border border-gray-700 overflow-hidden">
            <div className="border-b border-gray-700 px-6 py-4 bg-gradient-to-r from-gray-800 to-gray-750">
              <h3 className="text-lg font-semibold text-white flex items-center">
                <FaBell className="text-green-400 mr-2" />
                Actividad Reciente
              </h3>
            </div>
            
            <div className="p-6">
              {loading ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                </div>
              ) : actividadReciente.length > 0 ? (
                <div className="space-y-4">
                  {actividadReciente.map((actividad, index) => {
                    const IconComponent = actividad.icono;
                    const isLast = index === actividadReciente.length - 1;
                    
                    return (
                      <div key={index} className={`flex items-start ${!isLast ? 'border-b border-gray-700 pb-4' : ''}`}>
                        <div className={`bg-${actividad.color}-900 bg-opacity-30 rounded-full p-2 mr-3`}>
                          <IconComponent className={`text-${actividad.color}-400`} />
                        </div>
                        <div className="flex-1">
                          <p className="text-white font-medium">{actividad.titulo}</p>
                          <p className="text-gray-400 text-sm">{actividad.descripcion}</p>
                          <p className="text-gray-500 text-xs mt-1">{actividad.fechaTexto}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8">
                  <FaBell className="mx-auto text-gray-500 text-3xl mb-4" />
                  <p className="text-gray-400">No hay actividad reciente</p>
                  <p className="text-gray-500 text-sm">Sus actividades aparecerán aquí</p>
                </div>
              )}
              
              {/* Enlace para ver todo el historial */}
              {!loading && mantenimientos.length > 0 && (
                <div className="mt-6 text-center">
                  <button 
                    onClick={() => navigate('/mantenimientos')}
                    className="text-blue-400 hover:text-blue-300 text-sm font-medium"
                  >
                    Ver historial completo →
                  </button>
                </div>
              )}
            </div>
          </div>
          
          {/* Estadísticas en mini-tarjetas */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
            <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
              <h4 className="text-gray-400 text-sm mb-1">Vehículos registrados</h4>
              <p className="text-2xl font-bold text-white">{loading ? '...' : vehiculos.length}</p>
            </div>
            
            <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
              <h4 className="text-gray-400 text-sm mb-1">Citas completadas</h4>
              <p className="text-2xl font-bold text-white">
                {loading ? '...' : citas.filter(c => c.finalizada === true).length}
              </p>
            </div>
            
            <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
              <h4 className="text-gray-400 text-sm mb-1">Próxima cita</h4>
              <p className="text-white font-medium">
                {loading ? '...' : proximaCita ? moment(proximaCita.fecha_hora).format('DD/MM/YYYY') : 'Sin citas'}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserProfile;